import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Link, Typography, useTheme } from "@mui/material";

const DESCRIPTION_SX = {
  fontSize: 13,
  lineHeight: 1.55,
  fontWeight: 400,
  color: "text.secondary",
  width: "100%",
  wordBreak: "break-word",
};

/** Soft primary — readable as a link without competing with the title. */
const SEE_MORE_COLOR = "rgba(74, 44, 245, 0.62)";
const SEE_MORE_HOVER = "rgba(74, 44, 245, 0.9)";

const TOGGLE_SX = {
  border: "none",
  background: "none",
  p: 0,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.55,
  color: SEE_MORE_COLOR,
  cursor: "pointer",
  fontFamily: "inherit",
  textDecoration: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    color: SEE_MORE_HOVER,
    textDecoration: "underline",
    textUnderlineOffset: "2px",
  },
};

/**
 * Full-width company overview: collapses to a single row with a soft
 * "See more" control at the end of the line when text overflows.
 */
export default function ClampedDescription({
  text,
  expanded = false,
  onToggle,
  lines = 1,
}) {
  const theme = useTheme();
  const textRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const fadeColor =
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "#FFFFFF";

  const measure = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    // Slight tolerance for sub-pixel rounding.
    setTruncated(el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    measure();
  }, [measure, text, expanded, lines]);

  useEffect(() => {
    const el = textRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  if (!text) return null;

  const showSeeMore = !expanded && truncated;
  const singleLine = lines === 1;

  return (
    <Box data-testid="clamped-description" sx={{ width: "100%", minWidth: 0 }}>
      <Box sx={{ position: "relative", width: "100%" }}>
        <Typography
          ref={textRef}
          component="p"
          sx={{
            ...DESCRIPTION_SX,
            m: 0,
            ...(expanded
              ? { whiteSpace: "pre-wrap" }
              : singleLine
                ? {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    // Reserve space so copy doesn't sit under the control.
                    pr: showSeeMore ? "4.75rem" : 0,
                  }
                : {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: lines,
                    overflow: "hidden",
                    whiteSpace: "pre-wrap",
                  }),
          }}
        >
          {text}
        </Typography>

        {showSeeMore ? (
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              display: "inline-flex",
              alignItems: "center",
              pl: 2.5,
              background: `linear-gradient(90deg, transparent 0%, ${fadeColor} 32%)`,
            }}
          >
            <Link
              component="button"
              type="button"
              underline="none"
              onClick={onToggle}
              data-testid="clamped-description-toggle"
              sx={TOGGLE_SX}
            >
              See more
            </Link>
          </Box>
        ) : null}
      </Box>

      {expanded ? (
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={onToggle}
          data-testid="clamped-description-toggle"
          sx={{
            ...TOGGLE_SX,
            mt: 0.5,
            display: "inline-block",
          }}
        >
          See less
        </Link>
      ) : null}
    </Box>
  );
}

ClampedDescription.propTypes = {
  text: PropTypes.string,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  lines: PropTypes.number,
};
